import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCourseById } from '../../api/educentialApi';
import {
  Users,
  BookOpen,
  CheckCircle,
  Clock,
  TrendingUp,
  Award,
  ArrowLeft,
  BarChart3,
  Calendar,
  Target
} from 'lucide-react';
import './CourseAnalytics.css';

function CourseAnalytics() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCourseAnalytics();
  }, [id]);

  const loadCourseAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCourseById(id);
      if (response.success) {
        setCourse(response.data);
      }
    } catch (err) {
      console.error('Failed to load course analytics:', err);
      setError(err.message || 'Failed to load course analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="loading-state">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="analytics-container">
        <div className="error-state">
          <h2>Failed to Load Analytics</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button className="btn-secondary" onClick={() => navigate('/educator/courses')}>
              Back to Courses
            </button>
            <button className="btn-primary" onClick={loadCourseAnalytics}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalEnrollments = course._count?.Enrollments || 0;
  const totalLessons = course.Modules?.reduce((sum, module) => sum + (module.Lessons?.length || 0), 0) || 0;

  // Calculate mock completion rate (in real scenario, this would come from backend)
  const completionRate = totalEnrollments > 0 ? Math.round((totalEnrollments * 0.65)) : 0;
  const activeStudents = totalEnrollments > 0 ? Math.round(totalEnrollments * 0.75) : 0;
  const avgProgress = totalEnrollments > 0 ? 58 : 0; // Mock data

  return (
    <div className="analytics-container">
      {/* Back Button */}
      <div className="back-navigation">
        <button onClick={() => navigate('/educator/courses')} className="btn-back">
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Courses</span>
        </button>
      </div>

      {/* Page Header */}
      <div className="analytics-header">
        <div>
          <h1 className="analytics-title">{course.title}</h1>
          <p className="analytics-subtitle">Course Analytics & Performance</p>
        </div>
        <div className="header-actions">
          <button
            className="btn-view-course"
            onClick={() => navigate(`/courses/${course.id}`)}
          >
            <BookOpen className="w-4 h-4" />
            View Course
          </button>
          <button
            className="btn-edit"
            onClick={() => navigate(`/educator/courses/edit/${course.id}`)}
          >
            Edit Course
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <MetricCard
          label="Total Enrollments"
          value={totalEnrollments}
          icon={Users}
          color="teal"
        />
        <MetricCard
          label="Active Students"
          value={activeStudents}
          icon={UserCheck}
          color="emerald"
        />
        <MetricCard
          label="Completion Rate"
          value={`${completionRate}%`}
          icon={CheckCircle}
          color="blue"
        />
        <MetricCard
          label="Avg. Progress"
          value={`${avgProgress}%`}
          icon={TrendingUp}
          color="purple"
        />
      </div>

      <div className="analytics-content">
        {/* Course Overview */}
        <div className="overview-card">
          <div className="card-header">
            <h2 className="card-title">
              <BarChart3 className="w-5 h-5" />
              Course Overview
            </h2>
          </div>
          <div className="card-body">
            <div className="overview-stats">
              <div className="stat-item">
                <div className="stat-icon bg-teal-100">
                  <BookOpen className="w-5 h-5 text-teal-600" />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Modules</div>
                  <div className="stat-value">{course.Modules?.length || 0}</div>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-icon bg-blue-100">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Lessons</div>
                  <div className="stat-value">{totalLessons}</div>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-icon bg-purple-100">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Course Duration</div>
                  <div className="stat-value">{course.duration}h</div>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-icon bg-amber-100">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Published</div>
                  <div className="stat-value">{new Date(course.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Module Performance */}
        <div className="modules-card">
          <div className="card-header">
            <h2 className="card-title">
              <Target className="w-5 h-5" />
              Module Performance
            </h2>
          </div>
          <div className="card-body">
            {course.Modules && course.Modules.length > 0 ? (
              <div className="modules-list">
                {course.Modules.map((module, index) => {
                  const lessonCount = module.Lessons?.length || 0;
                  // Mock completion data
                  const moduleCompletion = Math.round(Math.random() * 40 + 45);

                  return (
                    <div key={module.id} className="module-item">
                      <div className="module-header">
                        <div className="module-info">
                          <span className="module-number">Module {index + 1}</span>
                          <h3 className="module-title">{module.title}</h3>
                        </div>
                        <span className="lesson-count">{lessonCount} lessons</span>
                      </div>
                      <div className="module-progress">
                        <div className="progress-info">
                          <span className="progress-label">Average Completion</span>
                          <span className="progress-percentage">{moduleCompletion}%</span>
                        </div>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${moduleCompletion}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <BookOpen className="w-12 h-12 text-gray-400" />
                <p>No modules in this course yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Student Engagement */}
        <div className="engagement-card">
          <div className="card-header">
            <h2 className="card-title">
              <Users className="w-5 h-5" />
              Student Engagement
            </h2>
          </div>
          <div className="card-body">
            {totalEnrollments > 0 ? (
              <div className="engagement-stats">
                <div className="engagement-item">
                  <div className="engagement-circle active">
                    <span className="engagement-value">{activeStudents}</span>
                  </div>
                  <div className="engagement-label">Active Students</div>
                  <div className="engagement-description">Currently learning</div>
                </div>
                <div className="engagement-item">
                  <div className="engagement-circle completed">
                    <span className="engagement-value">{completionRate}</span>
                  </div>
                  <div className="engagement-label">Completed</div>
                  <div className="engagement-description">Finished the course</div>
                </div>
                <div className="engagement-item">
                  <div className="engagement-circle inactive">
                    <span className="engagement-value">{totalEnrollments - activeStudents - completionRate}</span>
                  </div>
                  <div className="engagement-label">Inactive</div>
                  <div className="engagement-description">Not recently active</div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <Users className="w-12 h-12 text-gray-400" />
                <p>No students enrolled yet</p>
                <span className="text-sm text-gray-500 mt-2">Share your course to get enrollments</span>
              </div>
            )}
          </div>
        </div>

        {/* Performance Insights */}
        <div className="insights-card">
          <div className="card-header">
            <h2 className="card-title">
              <Award className="w-5 h-5" />
              Performance Insights
            </h2>
          </div>
          <div className="card-body">
            <div className="insights-list">
              {totalEnrollments > 10 && (
                <div className="insight-item success">
                  <CheckCircle className="w-5 h-5" />
                  <div>
                    <h4>Great Enrollment</h4>
                    <p>Your course has attracted {totalEnrollments} students</p>
                  </div>
                </div>
              )}
              {avgProgress > 50 && (
                <div className="insight-item success">
                  <TrendingUp className="w-5 h-5" />
                  <div>
                    <h4>High Engagement</h4>
                    <p>Students are making good progress through the material</p>
                  </div>
                </div>
              )}
              {totalLessons < 5 && (
                <div className="insight-item warning">
                  <Target className="w-5 h-5" />
                  <div>
                    <h4>Add More Content</h4>
                    <p>Consider adding more lessons to provide more value</p>
                  </div>
                </div>
              )}
              {totalEnrollments === 0 && (
                <div className="insight-item info">
                  <Users className="w-5 h-5" />
                  <div>
                    <h4>Publish Your Course</h4>
                    <p>Make your course public to start getting enrollments</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const MetricCard = ({ label, value, icon: Icon, color }) => {
  const colorClasses = {
    teal: 'bg-teal-100 text-teal-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600'
  };

  return (
    <div className="metric-card">
      <div className={`metric-icon ${colorClasses[color]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="metric-content">
        <div className="metric-label">{label}</div>
        <div className="metric-value">{value}</div>
      </div>
    </div>
  );
};

const UserCheck = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

export default CourseAnalytics;
